/**
 * The close button, and what is behind it (`04-launcher-and-patcher.md` section 1.3).
 *
 * The app keeps running in the tray when the window closes, so the download queue and the app's
 * own update check outlive it. Whether it does is the user's to decide; whether it *can* is the
 * desktop's, and a Steam Deck in Gaming Mode has no tray at all. The shell answers that second
 * question, and it is the answer the settings screen hangs the switch on: offering to keep an
 * app in a tray that does not exist is offering a window that closes into nothing.
 */

import * as React from 'react';
import { isDesktop } from '../platform/index.js';
import { native } from '../platform/tauri.js';

let available = false;
type Listener = (available: boolean) => void;
const listeners = new Set<Listener>();

/** Tells the shell what the close button should do, and learns whether there is a tray. */
export async function syncTray(closeToTray: boolean): Promise<void> {
  if (!isDesktop) return;
  try {
    const next = await native.trayConfigure(closeToTray);
    if (next === available) return;
    available = next;
    for (const listener of listeners) listener(available);
  } catch {
    // A shell older than this command is a shell whose window simply closes, which is the
    // behaviour the switch would have been describing anyway. Nothing to report.
  }
}

export function useTrayAvailable(): boolean {
  const [state, setState] = React.useState(available);
  React.useEffect(() => {
    listeners.add(setState);
    // The sync may have finished before the settings screen was ever opened.
    setState(available);
    return () => {
      listeners.delete(setState);
    };
  }, []);
  return state;
}
